import { uploadImage } from 'api/uploadImage';
import {
  ALLOW_IMAGE_SIZE,
  ALLOW_IMAGE_SIZE_MB,
  ALLOW_IMAGE_TYPES,
  BATCH_UPLOAD_FILE_NUMBER_LIMIT,
} from 'constants/imageUpload';
import { useToast } from 'modules/common/components/Toast';
import { useTranslation } from 'react-i18next';

import { useUser } from '../useUser';
import { useUserMedia } from '../useUserMedia';
import { UPLOAD_IMAGES_LIMITS } from '../user.config';

export const useImageUpload = ({ isUploading }: { isUploading: boolean }) => {
  const toast = useToast();
  const { data: userInfo } = useUser();
  const { data } = useUserMedia({ onError: undefined });
  const { t } = useTranslation('user', { keyPrefix: 'upload' });

  const userType = userInfo?.user_type;
  const uploadLimits = !!userType ? UPLOAD_IMAGES_LIMITS[userType] : 0;
  const userImagesNum = data?.length || 0;
  const allowUpload = userImagesNum < uploadLimits;

  const showUploadErrorToast = (title: string, description: string) => toast({ title, description, status: 'error' });

  const hasValidUploadLimits = (files: File[]): boolean => {
    if (!files) return false;

    if (isUploading) {
      showUploadErrorToast(t('in-progress'), t('in-progress-message'));
      return false;
    }

    if (files.length + userImagesNum > uploadLimits) {
      showUploadErrorToast(t('upload-limit'), t('upload-limit-message', { uploadLimits }));
      return false;
    }

    // check if the number of files selected to upload exceed the batch upload file number limit
    if (files.length > BATCH_UPLOAD_FILE_NUMBER_LIMIT) {
      showUploadErrorToast(t('batch-limit', { batchLimit: BATCH_UPLOAD_FILE_NUMBER_LIMIT }), t('batch-limit-message'));
      return false;
    }

    return true;
  };

  const filterOutInvalidFiles = (files: File[]): File[] => {
    const supportedFileType = t('file-type-supported');

    return files.filter((file) => {
      if (!file.size) return false;

      // This check is for edge cases where file extensions are not specified, e.g. slack screenshots
      if (!file.type) {
        showUploadErrorToast(t('file-type-missing', { fileName: file.name }), supportedFileType);
        return false;
      }
      if (!ALLOW_IMAGE_TYPES.includes(file.type)) {
        showUploadErrorToast(t('file-type-unsupported', { fileName: file.name }), supportedFileType);
        return false;
      }

      if (file.size > ALLOW_IMAGE_SIZE) {
        showUploadErrorToast(
          t('file-size', { fileName: file.name }),
          t('file-size-message', { allowSize: ALLOW_IMAGE_SIZE_MB }),
        );
        return false;
      }

      return true;
    });
  };

  const uploadImageFile = async (file: File) => {
    if (!allowUpload) return;
    try {
      const res = await uploadImage(file);
      return res;
    } catch (err) {
      console.error(err);
      const uploadErr = err as { message: string };
      showUploadErrorToast(t('upload-fail'), uploadErr.message);
    }
  };

  const showUploadSuccessToast = (uploadedImageCount: number) => {
    if (!uploadedImageCount) return;

    const title = uploadedImageCount > 1 ? t('upload-success-multiple', { uploadedImageCount }) : t('upload-success');
    toast({ title, status: 'success' });
  };

  return {
    showUploadSuccessToast,
    hasValidUploadLimits,
    filterOutInvalidFiles,
    uploadImageFile,
    allowUpload,
  };
};
